import type { Project } from '@phrasewright/types';

export interface ProjectCreationResult {
  success: boolean;
  project?: Project;
  error?: string;
}

/**
 * Open a folder picker and create a project from the selected folder
 */
export async function openFolderAndCreateProject(): Promise<ProjectCreationResult> {
  if (!('showDirectoryPicker' in window)) {
    // Fallback for browsers without File System Access API
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.webkitdirectory = true;
      input.multiple = true;
      input.onchange = (e) => {
        const files = (e.target as HTMLInputElement).files;
        if (files && files.length > 0) {
          const rootPath = files[0].webkitRelativePath.split('/')[0];
          const project: Project = {
            id: Date.now().toString(),
            name: rootPath,
            path: rootPath,
            storageMode: 'filesystem',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            status: 'active',
            settings: {
              defaultFileType: 'document',
              autoSave: { enabled: true, intervalMs: 30000 },
              revisions: { maxPerFile: 50, autoCleanupDays: 90 },
              search: {
                enabled: true,
                indexContent: true,
                indexMetadata: true,
              },
              organization: {
                documentsPath: 'documents/',
                phrasesPath: 'phrases/',
                templatesPath: 'templates/',
              },
            },
          };
          resolve({ success: true, project });
        } else {
          resolve({ success: false, error: 'No files selected' });
        }
      };

      // Handle user cancellation
      input.onclick = () => {
        setTimeout(() => {
          if (!input.files || input.files.length === 0) {
            resolve({ success: false, error: 'User cancelled' });
          }
        }, 100);
      };

      input.click();
    });
  }

  try {
    const dirHandle = await (window as any).showDirectoryPicker({
      mode: 'readwrite',
      startIn: 'documents',
    });

    const project: Project = {
      id: Date.now().toString(),
      name: dirHandle.name,
      path: dirHandle.name,
      storageMode: 'filesystem',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: 'active',
      settings: {
        defaultFileType: 'document',
        autoSave: { enabled: true, intervalMs: 30000 },
        revisions: { maxPerFile: 50, autoCleanupDays: 90 },
        search: { enabled: true, indexContent: true, indexMetadata: true },
        organization: {
          documentsPath: 'documents/',
          phrasesPath: 'phrases/',
          templatesPath: 'templates/',
        },
      },
    };

    return { success: true, project };
  } catch (error) {
    if ((error as Error).name === 'AbortError') {
      return { success: false, error: 'User cancelled' };
    }
    return { success: false, error: (error as Error).message };
  }
}
